import { useEffect, useMemo, useState } from 'react';
import { createNote, getNoteAudienceRoles, getNoteTargets, updateNote } from '../../../../api/v2/NotesApi';
import { GrantDto } from '../../../../dtos/GrantDto';
import { NoteDto } from '../../../../dtos/NoteDto';
import { NoteTargetDto } from '../../../../dtos/NoteTargetDto';
import { UserRole } from '../../../../dtos/UserRole';
import { roleLabels } from '../../../shared/tasklist/config/audienceConfig';
import { TreeSelectNode } from '../../../shared/TreeSelect';
import { useTriggerTargetTree } from '../../../tasklist/tabs/hooks/useTriggerTargetTree';

// One selectable audience role plus whether it is currently checked.
export interface AudienceRoleOption {
    value: UserRole;
    label: string;
    selected: boolean;
}

export interface AddNoteDialogControllerProps {
    grantData: GrantDto;
    visible: boolean;
    // Context the dialog was opened with. Providing a level locks the selector to that target.
    phaseId?: string | null;
    stepId?: string | null;
    // When set, the dialog edits this existing note instead of creating a new one.
    note?: NoteDto | null;
    onClose: () => void;
    onSaved?: () => void;
}

export interface AddNoteDialogViewProps {
    visible: boolean;
    title: string;
    saving: boolean;
    treeNodes: TreeSelectNode[];
    treeValue: string;
    selectedLabel: string;
    targetLocked: boolean;
    comment: string;
    noteDate: Date | null;
    // Audience picker: only shown when the user holds more than one role on the grant. A single role
    // is applied silently (empty list here → backend defaults to the author's role).
    showAudience: boolean;
    audienceRoles: AudienceRoleOption[];
    onToggleRole: (role: UserRole) => void;
    canSave: boolean;
    onTargetChange: (key: string) => void;
    onCommentChange: (text: string) => void;
    onDateChange: (date: Date | null) => void;
    onSave: () => void;
    onCancel: () => void;
}

export const useAddNoteDialogController = (props: AddNoteDialogControllerProps): AddNoteDialogViewProps => {
    const { grantData, visible, note, onClose, onSaved } = props;

    const [targets, setTargets] = useState<NoteTargetDto[]>([]);
    const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [noteDate, setNoteDate] = useState<Date | null>(null);
    const [saving, setSaving] = useState(false);
    // Roles the author can stamp; the subset they have checked. Both empty until the dialog loads.
    const [selectableRoles, setSelectableRoles] = useState<UserRole[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);

    const isEdit = !!note;

    // The opening context locks the target only when adding; editing always lets the user re-target.
    const targetLocked = !isEdit && (!!props.phaseId || !!props.stepId);

    // Seed the form from the note being edited (or the opening context) and load the target list
    // each time the dialog opens.
    useEffect(() => {
        if (!visible) return;
        setComment(note?.comment ?? '');
        setSelectedPhaseId(note?.phaseId ?? props.phaseId ?? null);
        setSelectedStepId(note?.stepId ?? props.stepId ?? null);
        setNoteDate(note?.dateCreated ? new Date(note.dateCreated) : new Date());

        if (!grantData?.id) return;
        const load = async () => {
            const [targetsResult, rolesResult] = await Promise.all([
                getNoteTargets(grantData.id),
                getNoteAudienceRoles(grantData.id),
            ]);
            if (targetsResult?.data) setTargets(targetsResult.data);

            const roles = rolesResult?.data ?? [];
            setSelectableRoles(roles);
            // A new note starts with every role checked; editing keeps the note's existing audience
            // (its stored roles, limited to ones the author still holds).
            setSelectedRoles(note ? roles.filter(r => note.audienceRoles.includes(r)) : roles);
        };
        load();
    }, [visible, props.phaseId, props.stepId, note, grantData?.id]);

    // Current tree selection key, derived from the chosen target (subtask wins over task). No
    // selection (empty) means the note applies to the grant.
    const treeValue = selectedStepId
        ? `step:${selectedStepId}`
        : selectedPhaseId
            ? `phase:${selectedPhaseId}`
            : '';

    // Reuse the trigger-target tree shape (module → task → subtask); note ids are Guids (strings).
    const treeItems = useMemo(
        () => targets.map(t => ({
            key: t.key,
            label: t.label,
            moduleName: t.moduleName,
            phaseName: t.phaseName,
            stepName: t.stepName ?? undefined,
            isStep: t.isStep,
            phaseId: t.phaseId,
            stepId: t.stepId ?? undefined,
        })),
        [targets]
    );

    const { nodes: treeNodes, selectedLabel } = useTriggerTargetTree(treeItems, treeValue);

    const onTargetChange = (key: string) => {
        const [kind, id] = key.split(':');
        if (kind === 'phase') {
            setSelectedPhaseId(id);
            setSelectedStepId(null);
        } else if (kind === 'step') {
            // A subtask anchors to its parent task so the note lands on the right timeline row.
            const target = targets.find(t => t.key === key);
            setSelectedPhaseId(target?.phaseId ?? null);
            setSelectedStepId(id);
        }
    };

    const onCommentChange = (text: string) => setComment(text);
    const onDateChange = (date: Date | null) => setNoteDate(date);

    // Only offer the picker when adding and there is a real choice (more than one role). Audience is
    // frozen at creation, so editing never shows it.
    const showAudience = !isEdit && selectableRoles.length > 1;

    const audienceRoles: AudienceRoleOption[] = useMemo(
        () => selectableRoles.map(r => ({
            value: r,
            label: roleLabels[r] ?? UserRole[r],
            selected: selectedRoles.includes(r),
        })),
        [selectableRoles, selectedRoles]
    );

    const onToggleRole = (role: UserRole) => {
        setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    // When the picker is shown the author must keep at least one role checked (a note for nobody).
    const canSave = comment.trim().length > 0 && !!noteDate && !saving
        && (!showAudience || selectedRoles.length > 0);

    const onSave = async () => {
        if (!canSave || !grantData?.id) return;
        setSaving(true);
        const request = {
            comment: comment.trim(),
            phaseId: selectedPhaseId,
            stepId: selectedStepId,
            date: noteDate,
            // Send the checked subset; a single-role author (no picker) sends the one role.
            audienceRoles: selectedRoles,
        };
        const result = isEdit
            ? await updateNote(note!.id, request)
            : await createNote(grantData.id, request);
        setSaving(false);

        if (result && !result.error) {
            onSaved?.();
            onClose();
        }
    };

    return {
        visible,
        title: isEdit ? 'Edit Note' : 'Add Note',
        saving,
        treeNodes,
        treeValue,
        selectedLabel,
        targetLocked,
        comment,
        noteDate,
        showAudience,
        audienceRoles,
        onToggleRole,
        canSave,
        onTargetChange,
        onCommentChange,
        onDateChange,
        onSave,
        onCancel: onClose,
    };
};
